import { useCallback } from 'react';

/**
 * Hook to standardize service unavailable error handling
 *
 * Provides utilities to detect and handle service unavailable errors
 * that occur when the backend is sleeping or unreachable.
 *
 * @param onServiceUnavailable - Callback to invoke when service is unavailable
 * @returns Object with error handling utilities
 */
export function useServiceUnavailable(onServiceUnavailable?: () => void) {
  /**
   * Check if an error is a service unavailable error
   * @param error - Error to check
   * @returns true if error indicates service unavailable
   */
  const isServiceUnavailableError = useCallback((error: any): boolean => {
    return error?.isServiceUnavailable === true;
  }, []);

  /**
   * Handle an error, calling onServiceUnavailable if appropriate
   * @param error - Error to handle
   */
  const handleError = useCallback((error: any) => {
    if (isServiceUnavailableError(error) && onServiceUnavailable) {
      onServiceUnavailable();
    }
  }, [isServiceUnavailableError, onServiceUnavailable]);

  /**
   * Wrap a fetch function to automatically handle service unavailable errors
   * @param fetchFn - Async function to wrap
   * @returns Wrapped function that handles errors
   */
  const wrapFetch = useCallback(<T>(fetchFn: () => Promise<T>) => {
    return async (): Promise<T> => {
      try {
        return await fetchFn();
      } catch (error: any) {
        handleError(error);
        throw error;
      }
    };
  }, [handleError]);

  return {
    handleError,
    wrapFetch,
    isServiceUnavailableError,
  };
}
